import { createContext, useCallback, useState } from "react";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogAction,
  AlertDialogClose,
} from "@/components/ui/alert-dialog";

export interface DialogOptions {
  title: string;
  description: string;
  onConfirm: () => void;
  onCancel: () => void;
  cancelLabel?: string;
  confirmLabel?: string;
}

export const DialogContext = createContext<{
  showConfirmationDialog: (options: DialogOptions) => void;
}>({
  showConfirmationDialog: () => {},
});

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [dialogOptions, setDialogOptions] = useState<DialogOptions | null>(
    null,
  );

  const showConfirmationDialog = useCallback(
    (options: DialogOptions) => {
      setDialogOptions(options);
    },
    [setDialogOptions],
  );
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setDialogOptions(null);
      }
    },
    [setDialogOptions],
  );

  return (
    <DialogContext.Provider value={{ showConfirmationDialog }}>
      {children}
      <AlertDialog open={!!dialogOptions} onOpenChange={handleOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{dialogOptions?.title}</AlertDialogTitle>
            <AlertDialogClose className="absolute top-2 right-2 text-gray-500" />
          </AlertDialogHeader>
          <hr className="-mx-6 my-1" />
          <AlertDialogDescription>
            {dialogOptions?.description}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogAction onClick={dialogOptions?.onConfirm}>
              {dialogOptions?.confirmLabel || "Confirm"}
            </AlertDialogAction>
            <AlertDialogCancel onClick={dialogOptions?.onCancel}>
              {dialogOptions?.cancelLabel || "Cancel"}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogContext.Provider>
  );
}
